import { Box } from '@mui/material';
import { useContentElement } from '@plugins/next-cms-core';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

export default function LocalBusinessStructuredDataBlock(props) {
  const { data, context: { page }, appData: { pageUrls } } = props;
  const router = useRouter();
  const { elementData } = useContentElement(
    data,
    LocalBusinessStructuredDataBlock.dataSchema,
  );

  const { branch } = elementData;
  const attrs = branch?.data?.attributes || {};

  const pageId = pageUrls.find((url) => url.locale === router.locale)?.id;

  const DAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  const normalizeDayName = (d) => {
    if (!d) return undefined;
    const lower = String(d).toLowerCase();
    return DAYS.find((day) => day.toLowerCase() === lower);
  };

  const getDaysBetween = (start, end) => {
    const sName = normalizeDayName(start);
    if (!sName) return [];
    if (!end) return [sName];
    const eName = normalizeDayName(end);
    if (!eName) return [sName];
    const s = DAYS.indexOf(sName);
    const e = DAYS.indexOf(eName);
    if (s === -1 || e === -1) return [sName];
    if (s <= e) return DAYS.slice(s, e + 1);
    return [...DAYS.slice(s), ...DAYS.slice(0, e + 1)];
  };

  const parseTimeWithLuxon = (val) => {
    if (!val) return undefined;
    const s = String(val).trim();

    let dt = DateTime.fromISO(s);

    if (!dt.isValid) {
      const formats = [
        'H:mm',
        'HH:mm',
        'H:mm:ss',
        'HH:mm:ss',
        'H:mm:ss.SSS',
        'HH:mm:ss.SSS',
      ];
      const foundFmt = formats.find((fmt) => DateTime.fromFormat(s, fmt).isValid);
      if (foundFmt) {
        dt = DateTime.fromFormat(s, foundFmt);
      }
    }

    return dt.isValid ? dt.toFormat('HH:mm:ss') : undefined;
  };

  const mapItemToDaySpecs = (item) => {
    if (!item) return [];
    const days = getDaysBetween(item.dayRangeStart, item.dayRangeEnd);
    if (!days.length) return [];

    const opens = parseTimeWithLuxon(item.dayTimeStart);
    const closes = parseTimeWithLuxon(item.dayTimeEnd);
    if (!opens && !closes) return [];

    return days.map((day) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: day,
      ...(opens ? { opens } : {}),
      ...(closes ? { closes } : {}),
    }));
  };

  function hoursFromList(list) {
    if (!Array.isArray(list)) return [];
    return list.flatMap((it) => mapItemToDaySpecs(it)).filter(Boolean);
  }

  const telephone = attrs.primaryPhone || attrs.salesPhone || undefined;
  const imageUrl = attrs.primaryImage?.data?.attributes?.url;

  const addressCountryVal = attrs.addressCountry
    ? (COUNTRY[attrs.addressCountry] || attrs.addressCountry)
    : undefined;

  const geoObj = (attrs.geocodeLat != null && attrs.geocodeLng != null)
    ? {
      '@type': 'GeoCoordinates',
      latitude: String(attrs.geocodeLat),
      longitude: String(attrs.geocodeLng),
    }
    : undefined;

  const openingHours = [
    ...hoursFromList(attrs.businessHoursWorkshop),
  ].filter(Boolean);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: attrs.name || undefined,
    address: {
      '@type': 'PostalAddress',
      postalCode: attrs.addressZipcode || undefined,
      streetAddress: attrs.addressStreet || undefined,
      addressLocality: attrs.addressCity || undefined,
      addressCountry: addressCountryVal,
      telephone: attrs.workshopPhone || undefined,
    },
    geo: geoObj,
    telephone,
    url: attrs.pageUrl ? absoluteUrl(attrs.pageUrl) : undefined,
    openingHoursSpecification: openingHours,
    image: imageUrl || undefined,
  };

  function absoluteUrl(u) {
    if (!u) return undefined;
    if (u.startsWith('http://') || u.startsWith('https://')) return u;

    return `https://www.schimmel-automobile.de${u}`;
  }

  return (
    <Box>
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        type="application/ld+json"
      />
    </Box>
  );
}

LocalBusinessStructuredDataBlock.typeName = 'ComponentContentLocalBusinessStructuredData';

const COUNTRY = {
  Deutschland: 'DE',
};

LocalBusinessStructuredDataBlock.propTypes = {
  data: PropTypes.shape({
    branch: PropTypes.shape({
      data: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        attributes: PropTypes.shape({
          name: PropTypes.string,
          addressStreet: PropTypes.string,
          addressZipcode: PropTypes.string,
          addressCity: PropTypes.string,
          addressCountry: PropTypes.string,
          pageUrl: PropTypes.string,
          geocodeLat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          geocodeLng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          primaryPhone: PropTypes.string,
          salesPhone: PropTypes.string,
          workshopPhone: PropTypes.string,
          primaryImage: PropTypes.shape({
            data: PropTypes.shape({
              attributes: PropTypes.shape({
                url: PropTypes.string,
              }),
            }),
          }),
          businessHoursSales: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
              dayRangeStart: PropTypes.string,
              dayRangeEnd: PropTypes.string,
              dayTimeStart: PropTypes.string,
              dayTimeEnd: PropTypes.string,
            }),
          ),
          businessHoursWorkshop: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
              dayRangeStart: PropTypes.string,
              dayRangeEnd: PropTypes.string,
              dayTimeStart: PropTypes.string,
              dayTimeEnd: PropTypes.string,
            }),
          ),
        }),
      }),
    }),
  }).isRequired,
};

LocalBusinessStructuredDataBlock.graphQlSchema = `
... on ${LocalBusinessStructuredDataBlock.typeName} {
  id
  branch {
    data {
      id
      attributes {
        addressCity
        addressCountry
        addressStreet
        addressZipcode
        appointmentUrl
        branchId
        createdAt
        geocodeLat
        geocodeLng
        importId
        locale
        name
        pageUrl
        primaryEmail
        primaryImage {
          data {
            id
            attributes {
              alternativeText
              caption
              width
              height
              url
            }
          }
        }
        primaryPhone
        publishedAt
        salesPhone
        updatedAt
        workshopEmail
        workshopPhone
        businessHoursSales {
          id
          dayRangeEnd
          dayRangeStart
          dayTimeEnd
          dayTimeStart
        }
        businessHoursWorkshop {
          id
          dayRangeEnd
          dayRangeStart
          dayTimeEnd
          dayTimeStart
        }
      }
    }
  }
}
`;
